import { useState, type ReactNode } from "react";
import {
  Stack,
  SimpleGrid,
  Text,
  Modal,
  Select,
  Textarea,
  Button,
  Group,
  Badge,
  ActionIcon,
  Box,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import {
  BODY_REGION_VALUES,
  SKIN_FINDING_TYPE_VALUES,
  INCIDENT_SEVERITY_VALUES,
} from "@livingsyncs/types";
import type {
  BodyRegion,
  SkinFindingType,
  IncidentSeverity,
} from "@livingsyncs/types";
// Review cycle 1 fix (fanout check, M2): a relative import, not the `@/`
// self-alias — packages/ui ships source and gets typechecked EMBEDDED in
// each consumer app's own compilation, which resolves `@/` against THAT
// consumer's own tsconfig path, not packages/ui's. frontend's `@/` happens
// to collide with a same-shaped file (its own formatters.ts duplicate), so
// this worked there by accident; admin/auditor have no such file and broke
// with "Cannot find module '@/utils/formatters'" (confirmed via
// `lsw fanout`). Matches this package's own convention elsewhere (e.g.
// EntityLookupField.tsx's relative imports) — never `@/` for an internal
// same-package import here.
import { SEVERITY_COLORS } from "../../../utils/formatters";

export interface BodyMapFinding {
  region: BodyRegion;
  findingType: SkinFindingType;
  severity: IncidentSeverity;
  note?: string;
}

export interface BodyMapFieldProps {
  label: ReactNode;
  description?: string;
  disabled?: boolean;
  error?: string;
  value: unknown;
  onChange: (value: BodyMapFinding[]) => void;
}

// US-188: 21-region taxonomy (BODY_REGION_VALUES) grouped into a front panel,
// a back panel, and "other" (a catch-all not tied to a diagram zone). MVP
// scope (spec §7): head/feet fold into named zones on front/back rather than
// separate panels — approximate positions on a simplified humanoid outline,
// not anatomically precise.
const FRONT_REGIONS: {
  region: BodyRegion;
  label: string;
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}[] = [
  { region: "head", label: "Head", cx: 100, cy: 30, rx: 22, ry: 26 },
  { region: "neck", label: "Neck", cx: 100, cy: 62, rx: 10, ry: 8 },
  { region: "chest", label: "Chest", cx: 100, cy: 100, rx: 32, ry: 28 },
  { region: "abdomen", label: "Abdomen", cx: 100, cy: 148, rx: 26, ry: 22 },
  { region: "groin", label: "Groin", cx: 100, cy: 182, rx: 16, ry: 12 },
  {
    region: "left_upper_arm",
    label: "Left upper arm",
    cx: 55,
    cy: 105,
    rx: 12,
    ry: 24,
  },
  {
    region: "right_upper_arm",
    label: "Right upper arm",
    cx: 145,
    cy: 105,
    rx: 12,
    ry: 24,
  },
  {
    region: "left_forearm",
    label: "Left forearm",
    cx: 42,
    cy: 150,
    rx: 10,
    ry: 22,
  },
  {
    region: "right_forearm",
    label: "Right forearm",
    cx: 158,
    cy: 150,
    rx: 10,
    ry: 22,
  },
  { region: "left_hand", label: "Left hand", cx: 36, cy: 185, rx: 9, ry: 12 },
  {
    region: "right_hand",
    label: "Right hand",
    cx: 164,
    cy: 185,
    rx: 9,
    ry: 12,
  },
  {
    region: "left_thigh",
    label: "Left thigh",
    cx: 82,
    cy: 225,
    rx: 16,
    ry: 30,
  },
  {
    region: "right_thigh",
    label: "Right thigh",
    cx: 118,
    cy: 225,
    rx: 16,
    ry: 30,
  },
  {
    region: "left_lower_leg",
    label: "Left lower leg",
    cx: 82,
    cy: 285,
    rx: 12,
    ry: 28,
  },
  {
    region: "right_lower_leg",
    label: "Right lower leg",
    cx: 118,
    cy: 285,
    rx: 12,
    ry: 28,
  },
];

const BACK_REGIONS: {
  region: BodyRegion;
  label: string;
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}[] = [
  {
    region: "back_upper",
    label: "Upper back",
    cx: 100,
    cy: 95,
    rx: 32,
    ry: 32,
  },
  {
    region: "back_lower",
    label: "Lower back",
    cx: 100,
    cy: 150,
    rx: 26,
    ry: 22,
  },
  { region: "buttocks", label: "Buttocks", cx: 100, cy: 190, rx: 26, ry: 18 },
  { region: "left_foot", label: "Left foot", cx: 82, cy: 340, rx: 12, ry: 14 },
  {
    region: "right_foot",
    label: "Right foot",
    cx: 118,
    cy: 340,
    rx: 12,
    ry: 14,
  },
];

const FINDING_TYPE_LABELS: Record<SkinFindingType, string> = {
  bruise: "Bruise",
  redness: "Redness",
  rash: "Rash",
  wound_laceration: "Wound / laceration",
  pressure_sore: "Pressure sore",
  swelling: "Swelling",
  discoloration: "Discoloration",
  other: "Other",
};

const SEVERITY_LABELS: Record<IncidentSeverity, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

const REGION_LABELS: Record<BodyRegion, string> = Object.fromEntries(
  [...FRONT_REGIONS, ...BACK_REGIONS].map((r) => [r.region, r.label]),
) as Record<BodyRegion, string>;
REGION_LABELS.other = "Other / unspecified";

// Dev-time completeness guard: catches a BODY_REGION_VALUES addition that
// forgot to update the diagram (or vice versa) instead of silently rendering
// "undefined" as a label.
if (import.meta.env.DEV) {
  const covered = new Set(Object.keys(REGION_LABELS));
  const missing = BODY_REGION_VALUES.filter((r: string) => !covered.has(r));
  if (missing.length > 0) {
    console.warn(
      `BodyMapField: BODY_REGION_VALUES has regions with no diagram entry: ${missing.join(", ")}`,
    );
  }
}

// Review cycle 1 fix (I6, WCAG 2.2 SC 2.5.8, AA): several regions' own
// visual footprint (neck, hands, forearms) falls under the 24x24 CSS px
// minimum touch-target size at this component's render scale (viewBox
// 200x360 inside a maxWidth:220 box, ~1.1x). Rather than enlarge the
// diagram's visible dots — which would distort the (already
// non-anatomical) silhouette and risk new visual overlaps between
// neighboring regions — every region gets an invisible, larger interactive
// ellipse layered on top of its decorative colored dot: the perceivable
// target includes both per the spec's own "visible and non-visible but
// perceivable parts" allowance. 13 viewBox units radius => ~28.6 CSS px
// diameter at 1.1x scale, clearing the 24px minimum with margin without
// growing so large that adjacent regions' hit zones start colliding.
const MIN_HIT_RADIUS = 13;

function BodyPanel({
  title,
  regions,
  markedRegions,
  onSelectRegion,
  disabled,
}: {
  title: string;
  regions: typeof FRONT_REGIONS;
  markedRegions: Map<BodyRegion, IncidentSeverity>;
  onSelectRegion: (region: BodyRegion) => void;
  disabled?: boolean;
}) {
  return (
    <Stack gap={4} align="center">
      <Text size="sm" fw={500}>
        {title}
      </Text>
      <Box style={{ width: "100%", maxWidth: 220 }}>
        <svg
          viewBox="0 0 200 360"
          role="group"
          aria-label={`${title} body diagram`}
          style={{ width: "100%", height: "auto" }}
        >
          {regions.map((r) => {
            const severity = markedRegions.get(r.region);
            const fill = severity
              ? `var(--mantine-color-${SEVERITY_COLORS[severity]}-4)`
              : "var(--mantine-color-gray-2)";
            const stroke = severity
              ? `var(--mantine-color-${SEVERITY_COLORS[severity]}-7)`
              : "var(--mantine-color-gray-5)";
            return (
              <g key={r.region}>
                {/* Decorative — the visible dot. Not interactive; the
                    larger hit-target ellipse below carries all a11y/click
                    attributes so it doesn't shadow them. */}
                <ellipse
                  cx={r.cx}
                  cy={r.cy}
                  rx={r.rx}
                  ry={r.ry}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={1.5}
                  aria-hidden="true"
                  style={{ pointerEvents: "none" }}
                />
                {/* Interactive hit target — invisible, at least MIN_HIT_RADIUS
                    in both axes, painted on top so it receives the click even
                    where it extends beyond the visible dot. */}
                <ellipse
                  cx={r.cx}
                  cy={r.cy}
                  rx={Math.max(r.rx, MIN_HIT_RADIUS)}
                  ry={Math.max(r.ry, MIN_HIT_RADIUS)}
                  fill="transparent"
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-label={`${r.label}${severity ? `, ${SEVERITY_LABELS[severity]} finding recorded` : ", no finding recorded"}`}
                  style={{ cursor: disabled ? "default" : "pointer" }}
                  onClick={() => !disabled && onSelectRegion(r.region)}
                  onKeyDown={(e) => {
                    if (!disabled && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      onSelectRegion(r.region);
                    }
                  }}
                >
                  <title>{r.label}</title>
                </ellipse>
              </g>
            );
          })}
        </svg>
      </Box>
    </Stack>
  );
}

/**
 * US-188: body-map field — tap a region on the front/back diagram to record
 * a skin finding (region + finding type + severity + optional note). An
 * empty array is a valid, complete "checked, nothing found" result, distinct
 * from never having opened the field.
 */
export function BodyMapField({
  label,
  description,
  disabled,
  error,
  value,
  onChange,
}: BodyMapFieldProps) {
  const findings: BodyMapFinding[] = Array.isArray(value)
    ? (value as BodyMapFinding[])
    : [];

  const [activeRegion, setActiveRegion] = useState<BodyRegion | null>(null);
  const [draftFindingType, setDraftFindingType] = useState<string | null>(null);
  const [draftSeverity, setDraftSeverity] = useState<string | null>(null);
  const [draftNote, setDraftNote] = useState("");

  const markedRegions = new Map<BodyRegion, IncidentSeverity>();
  for (const f of findings) {
    // Highest-severity finding wins when a region has more than one entry.
    const existing = markedRegions.get(f.region);
    if (
      !existing ||
      INCIDENT_SEVERITY_VALUES.indexOf(f.severity) >
        INCIDENT_SEVERITY_VALUES.indexOf(existing)
    ) {
      markedRegions.set(f.region, f.severity);
    }
  }

  function openRegion(region: BodyRegion) {
    setActiveRegion(region);
    setDraftFindingType(null);
    setDraftSeverity(null);
    setDraftNote("");
  }

  function addFinding() {
    if (!activeRegion || !draftFindingType || !draftSeverity) return;
    onChange([
      ...findings,
      {
        region: activeRegion,
        findingType: draftFindingType as SkinFindingType,
        severity: draftSeverity as IncidentSeverity,
        note: draftNote || undefined,
      },
    ]);
    setActiveRegion(null);
  }

  function removeFinding(index: number) {
    onChange(findings.filter((_, i) => i !== index));
  }

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        {label}
      </Text>
      {description && (
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <BodyPanel
          title="Front"
          regions={FRONT_REGIONS}
          markedRegions={markedRegions}
          onSelectRegion={openRegion}
          disabled={disabled}
        />
        <BodyPanel
          title="Back"
          regions={BACK_REGIONS}
          markedRegions={markedRegions}
          onSelectRegion={openRegion}
          disabled={disabled}
        />
      </SimpleGrid>

      <Button
        variant="subtle"
        size="xs"
        disabled={disabled}
        onClick={() => openRegion("other")}
      >
        + Add finding for an unlisted area
      </Button>

      {findings.length === 0 ? (
        <Text size="xs" c="dimmed">
          No findings recorded — checked, nothing found.
        </Text>
      ) : (
        <Stack gap={4}>
          {findings.map((f, i) => (
            <Group key={i} justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <Badge color={SEVERITY_COLORS[f.severity]} size="sm">
                  {SEVERITY_LABELS[f.severity]}
                </Badge>
                <Text size="sm">
                  {REGION_LABELS[f.region]} —{" "}
                  {FINDING_TYPE_LABELS[f.findingType]}
                  {f.note ? `: ${f.note}` : ""}
                </Text>
              </Group>
              {!disabled && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label={`Remove finding: ${REGION_LABELS[f.region]}`}
                  onClick={() => removeFinding(i)}
                >
                  <IconX size={14} />
                </ActionIcon>
              )}
            </Group>
          ))}
        </Stack>
      )}

      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}

      <Modal
        opened={activeRegion !== null}
        onClose={() => setActiveRegion(null)}
        title={
          activeRegion ? `Add finding — ${REGION_LABELS[activeRegion]}` : ""
        }
        size="sm"
      >
        <Stack gap="sm">
          <Select
            label="Finding type"
            placeholder="Select"
            required
            data={SKIN_FINDING_TYPE_VALUES.map((v) => ({
              value: v,
              label: FINDING_TYPE_LABELS[v],
            }))}
            value={draftFindingType}
            onChange={setDraftFindingType}
          />
          <Select
            label="Severity"
            placeholder="Select"
            required
            data={INCIDENT_SEVERITY_VALUES.map((v) => ({
              value: v,
              label: SEVERITY_LABELS[v],
            }))}
            value={draftSeverity}
            onChange={setDraftSeverity}
          />
          <Textarea
            label="Note"
            placeholder="Optional details"
            rows={3}
            value={draftNote}
            onChange={(e) => setDraftNote(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setActiveRegion(null)}>
              Cancel
            </Button>
            <Button
              onClick={addFinding}
              disabled={!draftFindingType || !draftSeverity}
            >
              Add finding
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
